// @flow
/** @jsx isolateComponent */

import { type Component, useEffect, useState } from 'react';
import { get, partial } from 'lodash';

import { Colors } from '@zapier/design-system';
import { Modal } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';

import Celebrate from './Celebrate';
import Reason from './Reason/Reason';

import type { Plan } from '@zapier/design-system-revenue';
import type { TrialExtensionReason } from '@zapier/billing';

const MODAL_STATE = {
  reasons: 'reasons',
  upgrade: 'upgrade',
  celebrateTrial: 'celebrateTrial',
  celebrateUpgrade: 'celebrateUpgrade',
};

type Props = {|
  appName?: string,
  ChangePlanContainer: Component<*>,
  canExtendTrial: boolean,
  didExtendTrial: boolean,
  onExtendTrial: () => void,
  upgradeToPlan: Plan,
  validationReason: TrialExtensionReason,
  onClose: () => void,
  onUpgradeFinish: () => {},
|};

const UpgradeModal = ({
  appName,
  ChangePlanContainer,
  canExtendTrial,
  didExtendTrial,
  onExtendTrial,
  upgradeToPlan,
  validationReason,
  onClose,
  onUpgradeFinish,
}: Props) => {
  const [modalState, setModalState] = useState(MODAL_STATE.reasons);

  useEffect(() => {
    if (didExtendTrial) {
      setModalState(MODAL_STATE.celebrateTrial);
    }
  }, [didExtendTrial]);

  const onUpgradeClick = () => {
    setModalState(MODAL_STATE.upgrade);
  };

  const onUpgradeComplete = () => {
    onUpgradeFinish();
    setModalState(MODAL_STATE.celebrateUpgrade);
  };

  const changePlanParams = {
    toPlanName: get(upgradeToPlan, ['name'], '').toLowerCase(),
    changeType: 'upgrade',
  };

  const stateToModalContents = {
    [MODAL_STATE.reasons]: (
      <Reason
        appName={appName}
        canExtendTrial={canExtendTrial}
        onClose={onClose}
        onExtendTrial={partial(onExtendTrial, validationReason)}
        onUpgradeClick={onUpgradeClick}
        plan={upgradeToPlan}
        reason={validationReason}
      />
    ),
    [MODAL_STATE.upgrade]: (
      <ChangePlanContainer
        onCancelChangePlan={onClose}
        onFinishUpgradePlan={onUpgradeComplete}
        params={changePlanParams}
        shouldNotLoadZaps={false}
      />
    ),
    [MODAL_STATE.celebrateTrial]: (
      <Celebrate onClose={onClose} plan={upgradeToPlan} reason="trial" />
    ),
    [MODAL_STATE.celebrateUpgrade]: (
      <Celebrate onClose={onClose} plan={upgradeToPlan} reason="upgrade" />
    ),
  };

  const modalContents = stateToModalContents[modalState];

  return (
    <Modal
      backgroundColor={Colors.grey000}
      closeOnOutsideClick={true}
      maxWidth={895}
      onClose={onClose}
    >
      <div css={{ backgroundColor: Colors.grey000 }}>{modalContents}</div>
    </Modal>
  );
};

export default UpgradeModal;
